import { Flex, Radio, Text } from '@mantine/core';
import { useId } from 'react';
import { type Control, useController } from 'react-hook-form';
import { Checkbox, TextInput } from 'react-hook-form-mantine';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { KycCertificationChoiceMode } from '@dealstack/shared/lib/generated';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import type { CertificationChoiceFormValues } from './CertifiableProof.types';

interface Props {
	control: Control<CertificationChoiceFormValues>;
	mode: KycCertificationChoiceMode;
	disabled?: boolean;
}

/** "I have a certified copy" (the default) or "Nominate a certifier", with the fields each one needs. */
export const CertificationChoiceFields = ({ control, mode, disabled }: Props) => {
	const tInvestor = useTranslationInvestor();
	// Each proof renders its own group, so the native radios need a page-unique name or the
	// browser unchecks one group when the other is checked.
	const radioGroupName = useId();
	const { field: modeField } = useController({ name: 'mode', control });

	return (
		<Flex
			direction="column"
			gap="0.75rem"
			data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_CHOICE}
		>
			<Text size="sm" c="dark.0">
				{tInvestor('investment.kyc.certification.choice_title', {
					defaultValue: 'This document must be a certified copy.',
				})}
			</Text>
			<Radio.Group
				name={radioGroupName}
				value={modeField.value}
				onChange={(value) => modeField.onChange(value)}
			>
				<Flex direction="column" gap="0.5rem">
					<Radio
						value={KycCertificationChoiceMode.SELF_ATTESTED}
						disabled={disabled}
						label={tInvestor('investment.kyc.certification.mode_self_attested', {
							defaultValue: 'I already have a certified copy',
						})}
						data-testid={
							INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_MODE_SELF_ATTESTED
						}
					/>
					<Radio
						value={KycCertificationChoiceMode.CERTIFIER}
						disabled={disabled}
						label={tInvestor('investment.kyc.certification.mode_certifier', {
							defaultValue: 'Nominate someone to certify it',
						})}
						description={tInvestor(
							'investment.kyc.certification.mode_certifier_description',
							{
								defaultValue:
									'We will email them a link to check your upload against the original and sign it.',
							}
						)}
						data-testid={
							INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_MODE_CERTIFIER
						}
					/>
				</Flex>
			</Radio.Group>
			{mode === KycCertificationChoiceMode.SELF_ATTESTED && (
				<Checkbox
					name="attested"
					control={control}
					disabled={disabled}
					label={tInvestor('investment.kyc.certification.attested_label', {
						defaultValue:
							'I confirm that the copy I am uploading has already been certified.',
					})}
					data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_ATTESTED}
				/>
			)}
			{mode === KycCertificationChoiceMode.CERTIFIER && (
				<Flex gap="0.5rem" wrap="wrap">
					<TextInput
						name="certifierName"
						control={control}
						disabled={disabled}
						size="md"
						sx={{ flex: 1, minWidth: '12rem' }}
						label={tInvestor('investment.kyc.certification.certifier_name', {
							defaultValue: "Certifier's name",
						})}
						data-testid={
							INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_CERTIFIER_NAME
						}
					/>
					<TextInput
						name="certifierEmail"
						control={control}
						disabled={disabled}
						type="email"
						size="md"
						sx={{ flex: 1, minWidth: '12rem' }}
						label={tInvestor('investment.kyc.certification.certifier_email', {
							defaultValue: "Certifier's email",
						})}
						data-testid={
							INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_CERTIFIER_EMAIL
						}
					/>
				</Flex>
			)}
		</Flex>
	);
};
